import React from 'react';
import { AlertCircle, ArrowRight, CheckCircle, Clock, MessageSquare, User } from 'lucide-react';
import { Button } from '@components/common/Button';
import {
  useApprovalRequestDetails,
  useApprovalStepResponses,
  useApprovalRequestStatus,
} from '@api/hooks/useApprovalRequests';
import type { ApprovalHistoryEvent } from '@schemas/api';

interface ApprovalChainReviewUIProps {
  approvalRequestId: string;
  extractionId: string;
  onApprovalComplete: () => void;
  onRejected: () => void;
  onClose: () => void;
  entityLabel?: string;
  entityTitle?: string;
}

export const ApprovalChainReviewUI: React.FC<ApprovalChainReviewUIProps> = ({
  approvalRequestId,
  extractionId: _extractionId,
  onApprovalComplete,
  onRejected,
  onClose,
  entityLabel = 'extraction',
  entityTitle = 'Extraction',
}) => {
  type LineLike = Record<string, unknown> & {
    line_number?: number | string;
    description?: string;
    sku?: string;
    po_number?: string;
    quantity?: number | string;
    quantity_ordered?: number | string;
    qty?: number | string;
    unit_price?: number | string;
    uom?: string | null;
    erp_item_id?: string | null;
    erp_item_name?: string | null;
    erp_account_id?: string | null;
    erp_account_name?: string | null;
  };

  const formatDisplayValue = (value: unknown): string => {
    if (value === null || value === undefined || value === '') {
      return 'Empty';
    }
    if (Array.isArray(value)) {
      return JSON.stringify(value, null, 2);
    }
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    if (typeof value === 'object') {
      return JSON.stringify(value, null, 2);
    }
    return String(value);
  };

  const toLineArray = (value: unknown): LineLike[] => {
    if (!Array.isArray(value)) {
      return [];
    }
    return value.filter((item): item is LineLike => Boolean(item) && typeof item === 'object');
  };

  const getLineLabel = (line: LineLike, index: number) => {
    const lineNumber = line.line_number ?? index + 1;
    return `Line ${lineNumber}`;
  };

  const renderLineSummary = (line: LineLike) => {
    const fields = [
      ['Description', line.description],
      ['SKU', line.sku],
      ['PO Number', line.po_number],
      ['Quantity', line.quantity ?? line.quantity_ordered ?? line.qty],
      ['Unit Price', line.unit_price],
      ['UOM', line.uom],
      ['ERP Item', line.erp_item_name ?? line.erp_item_id],
      ['ERP Account', line.erp_account_name ?? line.erp_account_id],
    ].filter(([, value]) => value !== null && value !== undefined && value !== '');

    if (fields.length === 0) {
      return <p className="text-sm text-gray-500">No line details recorded.</p>;
    }

    return (
      <div className="grid gap-2 sm:grid-cols-2">
        {fields.map(([label, value]) => (
          <div key={label} className="rounded bg-white px-3 py-2">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
              {label}
            </p>
            <p className="mt-1 text-sm text-gray-900">{formatDisplayValue(value)}</p>
          </div>
        ))}
      </div>
    );
  };

  const getCorrectionDisplayValue = (
    event: ApprovalHistoryEvent,
    field: string,
    stage: 'before' | 'after'
  ) => {
    if (stage === 'before') {
      return event.context_before?.[field];
    }
    return event.context_after?.[field] ?? event.corrections?.[field];
  };

  // Fetch approval request details
  const approvalQuery = useApprovalRequestDetails(approvalRequestId);

  // Fetch step responses (approval history)
  const stepResponsesQuery = useApprovalStepResponses(approvalRequestId);

  // Poll for status changes and detect completion
  useApprovalRequestStatus(approvalRequestId, (newStatus) => {
    if (newStatus === 'approved') {
      // Approval chain completed successfully
      onApprovalComplete();
    } else if (newStatus === 'rejected') {
      // Approval chain was rejected
      onRejected();
    }
  });

  const approval = approvalQuery.data;
  const historyEvents: ApprovalHistoryEvent[] = stepResponsesQuery.data || [];
  const decisionEvents = historyEvents.filter((event) => event.event_type === 'decision');
  const submittedDecisionEvents = decisionEvents.filter((event) => Boolean(event.decision));
  const correctionEvents = historyEvents.filter((event) => event.event_type === 'correction');
  const dynamicErpFieldEntries = Object.entries(
    ((approval?.context_data?.dynamic_erp_fields || {}) as Record<string, any>)
  );
  const filteredContextEntries = Object.entries(approval?.context_data ?? {}).filter(
    ([key]) => {
      const normalizedKey = key.toLowerCase().replace(/[^a-z0-9]/g, '');
      return !new Set([
        'dynamicerpfields',
        'documentid',
        'docid',
        'vendor',
        'vendorid',
        'vendorname',
      ]).has(normalizedKey);
    }
  );

  if (approvalQuery.isLoading) {
    return (
      <div className="p-6 space-y-4">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-gray-200 rounded w-3/4"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
        </div>
      </div>
    );
  }

  if (!approval) {
    return (
      <div className="p-6">
        <div className="text-red-600 flex items-center gap-2">
          <AlertCircle size={20} />
          Failed to load approval request
        </div>
      </div>
    );
  }

  // Determine step status
  const isApproved = approval.status === 'approved';
  const isRejected = approval.status === 'rejected';
  const isPending = approval.status === 'pending';

  return (
    <div className="space-y-6">
      {/* Status Banner */}
      {isApproved && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
          <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={20} />
          <div>
            <h3 className="font-semibold text-green-900">
              {entityTitle} Approved
            </h3>
            <p className="text-sm text-green-700 mt-1">
              This {entityLabel} has been approved by all required approvers and is
              ready for the next processing stage.
            </p>
          </div>
        </div>
      )}

      {isRejected && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
          <div>
            <h3 className="font-semibold text-red-900">{entityTitle} Rejected</h3>
            <p className="text-sm text-red-700 mt-1">
              This {entityLabel} has been rejected during the approval process.
            </p>
          </div>
        </div>
      )}

      {isPending && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
          <Clock className="text-blue-600 flex-shrink-0 mt-0.5" size={20} />
          <div>
            <h3 className="font-semibold text-blue-900">Approval in Progress</h3>
            <p className="text-sm text-blue-700 mt-1">
              This {entityLabel} is currently undergoing approval chain review.
              Step {approval.current_step} of {approval.total_steps || '?'}.
            </p>
          </div>
        </div>
      )}

      {/* Approval Chain Progress */}
      <div className="space-y-4">
        <h3 className="font-semibold text-gray-900">Approval Chain Progress</h3>

        {/* Step-by-Step Display */}
        <div className="space-y-3">
          {Array.from({ length: approval.total_steps || 1 }).map((_, idx) => {
            const stepNum = idx + 1;
            const stepResps = decisionEvents.filter(
              (response) => response.step_number === stepNum
            );
            const hasApprovedDecision = stepResps.some(
              (response) => response.decision === 'approved'
            );
            const hasRejectedDecision = stepResps.some(
              (response) => response.decision === 'rejected'
            );
            const isCurrentStep = isPending && stepNum === approval.current_step;
            const stepCompleted = isApproved
              ? hasApprovedDecision || stepNum <= (approval.current_step ?? 0)
              : stepNum < approval.current_step;
            const visibleStepResps = stepCompleted
              ? stepResps.filter((response) => Boolean(response.decision))
              : stepResps;

            return (
              <div
                key={stepNum}
                className={`border rounded-lg p-4 transition-colors ${
                  stepCompleted
                    ? 'bg-green-50 border-green-200'
                    : isCurrentStep
                    ? 'bg-blue-50 border-blue-200'
                    : 'bg-gray-50 border-gray-200'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div
                      className={`flex items-center justify-center w-6 h-6 rounded-full flex-shrink-0 font-semibold text-sm ${
                        stepCompleted
                          ? 'bg-green-600 text-white'
                          : isCurrentStep
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-300 text-gray-700'
                      }`}
                    >
                      {stepNum}
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">Step {stepNum}</p>
                      {stepCompleted && (
                        <p className="text-sm text-green-700">Approved</p>
                      )}
                      {isRejected && stepNum === approval.current_step && hasRejectedDecision && (
                        <p className="text-sm text-red-700">Rejected</p>
                      )}
                      {isCurrentStep && !stepCompleted && (
                        <p className="text-sm text-blue-700">Awaiting approval</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Approver Responses */}
                {visibleStepResps.length > 0 && (
                  <div className="mt-3 space-y-2 ml-9">
                    {visibleStepResps.map((response) => (
                      <div key={response.step_id} className="text-sm">
                        <div className="flex items-center gap-2">
                          <User size={14} className="text-gray-500" />
                          <span className="font-medium text-gray-900">
                            {response.approver_name || response.approver_id}
                          </span>
                          <span
                            className={`inline-flex items-center gap-1 ${
                              response.decision === 'approved'
                                ? 'text-green-700'
                                : response.decision === 'rejected'
                                ? 'text-red-700'
                                : 'text-gray-500'
                            }`}
                          >
                            {response.decision === 'approved' && (
                              <>
                                <CheckCircle size={14} />
                                Approved
                              </>
                            )}
                            {response.decision === 'rejected' && (
                              <>
                                <AlertCircle size={14} />
                                Rejected
                              </>
                            )}
                            {!response.decision && (
                              <>
                                <Clock size={14} />
                                Pending
                              </>
                            )}
                          </span>
                        </div>
                        {response.responded_at && (
                          <p className="text-xs text-gray-500 ml-6">
                            {new Date(response.responded_at).toLocaleString()}
                          </p>
                        )}
                        {response.comments && (
                          <div className="ml-6 mt-1 text-gray-700 bg-white p-2 rounded text-xs">
                            <div className="flex items-start gap-2">
                              <MessageSquare size={12} className="mt-0.5" />
                              <span>{response.comments}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Approval Activity Timeline */}
      <div className="space-y-4">
        <h3 className="font-semibold text-gray-900">Approval Activity</h3>
        {submittedDecisionEvents.length === 0 && correctionEvents.length === 0 ? (
          <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-4 text-sm text-gray-600">
            No approval activity has been recorded yet.
          </div>
        ) : (
          <div className="space-y-3">
            {[...submittedDecisionEvents, ...correctionEvents]
              .sort(
                (left, right) =>
                  new Date(left.created_at).getTime() - new Date(right.created_at).getTime()
              )
              .map((event) => (
              <div
                key={event.id}
                className="rounded-lg border border-gray-200 bg-white p-4"
              >
                {event.event_type === 'decision' ? (
                  <div className="space-y-2">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-2">
                        <User size={16} className="mt-0.5 text-gray-500" />
                        <div>
                          <p className="font-medium text-gray-900">
                            {event.approver_name || event.approver_id}
                          </p>
                          <p className="text-sm text-gray-600">
                            Step {event.step_number ?? '?'} decision
                          </p>
                        </div>
                      </div>
                      <span
                        className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ${
                          event.decision === 'approved'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {event.decision === 'approved' ? (
                          <CheckCircle size={12} />
                        ) : (
                          <AlertCircle size={12} />
                        )}
                        {event.decision === 'approved' ? 'Approved' : 'Rejected'}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500">
                      {new Date(event.created_at).toLocaleString()}
                    </p>
                    {event.comments && (
                      <div className="rounded bg-gray-50 p-3 text-sm text-gray-700">
                        <div className="flex items-start gap-2">
                          <MessageSquare size={14} className="mt-0.5 text-gray-500" />
                          <span>{event.comments}</span>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="font-medium text-gray-900">
                          {event.corrected_by_name || event.corrected_by_user_id}
                        </p>
                        <p className="text-sm text-gray-600">
                          Applied corrections
                          {event.step_number ? ` at step ${event.step_number}` : ''}
                          {event.corrected_by_role ? ` as ${event.corrected_by_role}` : ''}
                        </p>
                      </div>
                      <span className="rounded-full bg-amber-100 px-2.5 py-1 text-xs font-medium text-amber-800">
                        Correction
                      </span>
                    </div>
                    <p className="text-xs text-gray-500">
                      {new Date(event.created_at).toLocaleString()}
                    </p>
                    {(event.corrected_fields || []).length > 0 && (
                      <div className="space-y-2">
                        {(event.corrected_fields || []).map((field) => {
                          const beforeValue = getCorrectionDisplayValue(event, field, 'before');
                          const afterValue = getCorrectionDisplayValue(event, field, 'after');
                          const beforeLines = field === 'lines' ? toLineArray(beforeValue) : [];
                          const afterLines = field === 'lines' ? toLineArray(afterValue) : [];

                          return (
                            <div
                              key={`${event.id}-${field}`}
                              className="rounded border border-gray-200 bg-gray-50 p-3"
                            >
                              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                                {field.replace(/_/g, ' ')}
                              </p>
                              {field === 'lines' ? (
                                <div className="mt-3 grid gap-3 lg:grid-cols-2">
                                  <div className="space-y-2">
                                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                                      Before
                                    </p>
                                    {beforeLines.length === 0 ? (
                                      <div className="rounded border border-dashed border-gray-300 bg-white p-3 text-sm text-gray-500">
                                        No lines recorded.
                                      </div>
                                    ) : (
                                      beforeLines.map((line, index) => (
                                        <div
                                          key={`before-${event.id}-${index}`}
                                          className="space-y-2 rounded border border-gray-200 bg-white p-3"
                                        >
                                          <p className="text-sm font-semibold text-gray-900">
                                            {getLineLabel(line, index)}
                                          </p>
                                          {renderLineSummary(line)}
                                        </div>
                                      ))
                                    )}
                                  </div>
                                  <div className="space-y-2">
                                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                                      After
                                    </p>
                                    {afterLines.length === 0 ? (
                                      <div className="rounded border border-dashed border-gray-300 bg-white p-3 text-sm text-gray-500">
                                        No lines recorded.
                                      </div>
                                    ) : (
                                      afterLines.map((line, index) => (
                                        <div
                                          key={`after-${event.id}-${index}`}
                                          className="space-y-2 rounded border border-amber-200 bg-amber-50 p-3"
                                        >
                                          <p className="text-sm font-semibold text-gray-900">
                                            {getLineLabel(line, index)}
                                          </p>
                                          {renderLineSummary(line)}
                                        </div>
                                      ))
                                    )}
                                  </div>
                                </div>
                              ) : (
                                <div className="mt-1 flex items-start gap-2 text-sm text-gray-700">
                                  <pre className="whitespace-pre-wrap break-words font-sans text-sm">
                                    {formatDisplayValue(beforeValue)}
                                  </pre>
                                  <ArrowRight size={14} className="mt-0.5 text-gray-400" />
                                  <pre className="whitespace-pre-wrap break-words font-sans text-sm font-medium text-gray-900">
                                    {formatDisplayValue(afterValue)}
                                  </pre>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        {correctionEvents.length > 0 && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
            {correctionEvents.length} correction
            {correctionEvents.length === 1 ? '' : 's'} recorded during this approval.
          </div>
        )}
      </div>

      {dynamicErpFieldEntries.length > 0 && (
        <div className="space-y-2">
          <h4 className="font-semibold text-sm text-gray-900">
            ERP Fields
          </h4>
          <div className="grid gap-2 sm:grid-cols-2">
            {dynamicErpFieldEntries.map(([key, field]) => (
              <div key={key} className="rounded bg-gray-50 px-3 py-2 text-sm">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                  {field?.label || key}
                </p>
                <p className="mt-1 text-gray-900">{formatDisplayValue(field?.value)}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Context Data Display */}
      {filteredContextEntries.length > 0 && (
        <div className="space-y-2">
          <h4 className="font-semibold text-sm text-gray-900">
            Approval Context
          </h4>
          <div className="bg-gray-50 rounded p-3 text-sm space-y-1">
            {filteredContextEntries.map(([key, value]) => (
              <div key={key} className="flex justify-between text-gray-700">
                <span className="font-medium">{key}:</span>
                <span className="text-right">{String(value)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {isPending && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-800">
          This approval chain is in progress. Decisions are routed through the current workflow assignment, and this view reflects the live request state.
        </div>
      )}

      {/* Completion Message */}
      {(isApproved || isRejected) && (
        <div className="flex gap-3 pt-6 border-t">
          <Button
            onClick={onClose}
            className="flex-1 bg-gray-800 hover:bg-gray-900 text-white"
          >
            Close
          </Button>
        </div>
      )}
    </div>
  );
};
